import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Image as ImageIcon, Upload, Rocket, RotateCcw, X,
  CheckCircle2, CircleDot, AlertCircle, Sparkles, ChevronRight, Loader2
} from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n";
import { useImages } from "../../lib/images";

/**
 * Full CMS-style image manager.
 *
 * UX flow:
 *   1. Sidebar lists every page. First page is auto-selected.
 *   2. Main area shows a card per image slot on that page.
 *   3. Each card supports drag-and-drop OR click-to-upload.
 *   4. Uploads go DIRECTLY to Cloudinary (signed upload) → we then POST the
 *      returned url to /api/admin/images/{key}/draft.
 *   5. Draft mode: preview is shown with a yellow badge until the user hits
 *      "Publicar" — then the site public API reflects the new image.
 */
export default function ImagesEditor() {
  const { pick, lang } = useI18n();
  const { reload: reloadPublic } = useImages();

  const [pages, setPages] = useState([]);
  const [slots, setSlots] = useState([]);
  const [activePage, setActivePage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/images");
      setPages(r.data?.pages || []);
      setSlots(r.data?.slots || []);
      if (!activePage && r.data?.pages?.length) setActivePage(r.data.pages[0].key);
    } catch (e) {
      toast.error(pick("No se pudieron cargar las imágenes", "Failed to load images", "Falha ao carregar as imagens"));
    } finally {
      setLoading(false);
    }
  }, [activePage, pick]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pageSlots = useMemo(() => slots.filter((s) => s.page === activePage), [slots, activePage]);
  const totalDrafts = useMemo(() => slots.filter((s) => s.has_draft).length, [slots]);
  const pageDrafts = useMemo(() => pageSlots.filter((s) => s.has_draft).length, [pageSlots]);

  const uploadOneFile = async (slotKey, file) => {
    const isVideo = file.type.startsWith("video/");
    const resource_type = isVideo ? "video" : "image";
    const sigRes = await api.get("/admin/cloudinary/signature", {
      params: { folder: `xega/${slotKey.replace(/\./g, "_")}`, resource_type },
    });
    const sig = sigRes.data;
    const form = new FormData();
    form.append("file", file);
    form.append("api_key", sig.api_key);
    form.append("timestamp", sig.timestamp);
    form.append("signature", sig.signature);
    form.append("folder", sig.folder);
    const endpoint = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resource_type}/upload`;
    const cloudRes = await fetch(endpoint, { method: "POST", body: form });
    const uploaded = await cloudRes.json();
    if (!cloudRes.ok || !uploaded.secure_url) {
      throw new Error(uploaded.error?.message || "Cloudinary upload failed");
    }
    return { url: uploaded.secure_url, public_id: uploaded.public_id, resource_type };
  };

  const uploadFile = async (slotKey, file) => {
    const uploaded = await uploadOneFile(slotKey, file);
    // Save legacy single-image draft (slot #0 fallback)
    const draftRes = await api.post(`/admin/images/${slotKey}/draft`, {
      url: uploaded.url,
      public_id: uploaded.public_id,
      resource_type: uploaded.resource_type,
    });
    return draftRes.data?.slot;
  };

  /** Save the full slideshow config (up to 4 slides + interval) as a draft. */
  const saveSlideshow = async (slotKey, slides, intervalMs) => {
    const cleaned = (slides || [])
      .filter((s) => s && s.url)
      .map((s) => ({
        url: s.url,
        public_id: s.public_id || null,
        resource_type: s.resource_type || "image",
      }));
    const r = await api.put(`/admin/images/${slotKey}/slideshow`, {
      slides: cleaned,
      interval_ms: Number(intervalMs) || 5000,
    });
    setSlots((prev) => prev.map((s) => (s.key === slotKey ? r.data.slot : s)));
    return r.data?.slot;
  };

  /** Upload one file → set slide at position `idx` in the draft slideshow. */
  const handleSlideUpload = async (slotKey, idx, file) => {
    if (!file) return;
    const slot = slots.find((s) => s.key === slotKey);
    if (!slot) return;
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      toast.error(pick("Solo se aceptan imágenes en las diapositivas", "Only images accepted in slides", "Apenas imagens aceitas nas slides"));
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error(pick("Máximo 8 MB", "Max 8 MB", "Máximo 8 MB"));
      return;
    }
    setBusyKey(slotKey);
    try {
      const uploaded = await uploadOneFile(slotKey, file);
      const current = [...(slot.draft_slides?.length ? slot.draft_slides : slot.published_slides || [])];
      // Ensure we have an array of length >= idx+1
      while (current.length < idx + 1) current.push(null);
      current[idx] = { url: uploaded.url, public_id: uploaded.public_id, resource_type: uploaded.resource_type };
      const cleaned = current.filter(Boolean);
      const intervalMs = slot.draft_interval_ms || slot.published_interval_ms || 5000;
      await saveSlideshow(slotKey, cleaned, intervalMs);
      toast.success(pick("Diapositiva cargada", "Slide uploaded", "Slide enviado"));
    } catch (e) {
      toast.error(e.message || pick("Falló la carga", "Upload failed", "Falha no envio"));
    } finally {
      setBusyKey(null);
    }
  };

  /** Remove a slide from the draft slideshow (does not touch Cloudinary — publish/discard will). */
  const handleSlideRemove = async (slotKey, idx) => {
    const slot = slots.find((s) => s.key === slotKey);
    if (!slot) return;
    const current = [...(slot.draft_slides?.length ? slot.draft_slides : slot.published_slides || [])];
    current.splice(idx, 1);
    setBusyKey(slotKey);
    try {
      const intervalMs = slot.draft_interval_ms || slot.published_interval_ms || 5000;
      await saveSlideshow(slotKey, current.filter(Boolean), intervalMs);
      toast.success(pick("Diapositiva quitada", "Slide removed", "Slide removido"));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error";
      toast.error(msg);
    } finally {
      setBusyKey(null);
    }
  };

  const handleIntervalChange = async (slotKey, intervalMs) => {
    const slot = slots.find((s) => s.key === slotKey);
    if (!slot) return;
    const current = slot.draft_slides?.length ? slot.draft_slides : (slot.published_slides || []);
    try {
      await saveSlideshow(slotKey, [...current], intervalMs);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error";
      toast.error(msg);
    }
  };

  const handleFile = async (slotKey, file) => {
    if (!file) return;
    const slot = slots.find((s) => s.key === slotKey);
    const mediaType = slot?.media_type || "image";
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    // Validate against slot type
    if (mediaType === "image" && !isImage) {
      toast.error(pick("Este slot solo acepta imágenes", "This slot only accepts images", "Este slot só aceita imagens"));
      return;
    }
    if (mediaType === "video" && !isVideo) {
      toast.error(pick("Este slot solo acepta videos", "This slot only accepts videos", "Este slot só aceita vídeos"));
      return;
    }
    if (mediaType === "mixed" && !isImage && !isVideo) {
      toast.error(pick("Solo imágenes o videos", "Images or videos only", "Apenas imagens ou vídeos"));
      return;
    }
    const maxMB = isVideo ? 40 : 8;
    if (file.size > maxMB * 1024 * 1024) {
      toast.error(pick(`Máximo ${maxMB} MB`, `Max ${maxMB} MB`, `Máximo ${maxMB} MB`));
      return;
    }
    setBusyKey(slotKey);
    try {
      const updated = await uploadFile(slotKey, file);
      setSlots((prev) => prev.map((s) => (s.key === slotKey ? updated : s)));
      toast.success(pick("Contenido cargado como borrador", "Uploaded as draft", "Enviado como rascunho"));
    } catch (e) {
      toast.error(e.message || pick("Falló la carga", "Upload failed", "Falha no envio"));
    } finally {
      setBusyKey(null);
    }
  };

  const publish = async (slotKey) => {
    setBusyKey(slotKey);
    try {
      const r = await api.post(`/admin/images/${slotKey}/publish`);
      setSlots((prev) => prev.map((s) => (s.key === slotKey ? r.data.slot : s)));
      reloadPublic();
      toast.success(pick("Publicado en el sitio", "Published live", "Publicado no site"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusyKey(null);
    }
  };

  const publishAll = async () => {
    if (!totalDrafts) return;
    setBusyKey("__all__");
    try {
      await api.post("/admin/images/publish-all");
      await load();
      reloadPublic();
      toast.success(pick(`Publicados ${totalDrafts} cambios`, `Published ${totalDrafts} changes`, `Publicados ${totalDrafts} alterações`));
    } catch (e) {
      toast.error("Error");
    } finally {
      setBusyKey(null);
    }
  };

  const discard = async (slotKey) => {
    setBusyKey(slotKey);
    try {
      const r = await api.post(`/admin/images/${slotKey}/discard`);
      setSlots((prev) => prev.map((s) => (s.key === slotKey ? r.data.slot : s)));
      toast.success(pick("Borrador descartado", "Draft discarded", "Rascunho descartado"));
    } catch (e) {
      toast.error("Error");
    } finally {
      setBusyKey(null);
    }
  };

  const reset = async (slotKey) => {
    if (!window.confirm(pick("¿Restaurar imagen original?", "Reset to original image?", "Restaurar imagem original?"))) return;
    setBusyKey(slotKey);
    try {
      const r = await api.post(`/admin/images/${slotKey}/reset`);
      setSlots((prev) => prev.map((s) => (s.key === slotKey ? r.data.slot : s)));
      reloadPublic();
      toast.success(pick("Restaurado", "Reset done", "Restaurado"));
    } catch (e) {
      toast.error("Error");
    } finally {
      setBusyKey(null);
    }
  };

  const activePageMeta = pages.find((p) => p.key === activePage);

  return (
    <div className="text-white" data-testid="admin-images-editor">
      {/* Header */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-400/30 bg-cyan-400/10 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
            <span className="label-mono !text-cyan-300 text-[10px]">
              {pick("BIBLIOTECA VISUAL", "VISUAL LIBRARY", "BIBLIOTECA VISUAL")}
            </span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl chrome-text leading-tight">
            {pick("Editor de imágenes", "Image editor", "Editor de imagens")}
          </h1>
          <p className="text-white/60 text-sm mt-2 max-w-xl">
            {pick(
              "Selecciona una página en la izquierda y reemplaza cualquier imagen con un solo click. Los cambios se guardan como borrador hasta que los publiques.",
              "Pick a page on the left and replace any image with one click. Changes are saved as drafts until you publish.",
              "Selecione uma página à esquerda e substitua qualquer imagem em um clique. As alterações ficam como rascunho até você publicar."
            )}
          </p>
        </div>
        {totalDrafts > 0 && (
          <button
            onClick={publishAll}
            disabled={busyKey === "__all__"}
            data-testid="publish-all-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 text-black font-tech uppercase tracking-wider text-xs shadow-[0_0_28px_-4px_rgba(0,240,255,0.7)] hover:shadow-[0_0_36px_-4px_rgba(0,240,255,0.95)] transition disabled:opacity-60">
            {busyKey === "__all__" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            {pick(`Publicar ${totalDrafts} cambios`, `Publish ${totalDrafts} changes`, `Publicar ${totalDrafts} alterações`)}
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-24 text-center text-cyan-300 label-mono">
          <Loader2 className="w-6 h-6 animate-spin inline-block" />
        </div>
      ) : (
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Sidebar: pages */}
          <aside className="lg:col-span-3">
            <div className="glass-card chrome-border p-3 sticky top-8">
              <div className="label-mono text-[10px] px-2 pt-1 pb-3">{pick("PÁGINAS", "PAGES", "PÁGINAS")}</div>
              <div className="space-y-1">
                {pages.map((p) => {
                  const draftCount = slots.filter((s) => s.page === p.key && s.has_draft).length;
                  const total = slots.filter((s) => s.page === p.key).length;
                  const active = activePage === p.key;
                  return (
                    <button
                      key={p.key}
                      onClick={() => setActivePage(p.key)}
                      data-testid={`page-tab-${p.key}`}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg transition text-left ${
                        active
                          ? "bg-cyan-400/10 border border-cyan-400/30 text-cyan-300"
                          : "border border-transparent text-white/70 hover:bg-white/5 hover:text-white"
                      }`}>
                      <div className="min-w-0">
                        <div className="font-tech text-sm truncate">{p[`label_${lang}`] || p.label_es}</div>
                        <div className="text-[10px] text-white/40 mt-0.5">{total} {pick("imágenes", "images", "imagens")}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        {draftCount > 0 && (
                          <span className="text-[9px] font-tech uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-400/20 border border-amber-400/40 text-amber-200">
                            {draftCount}
                          </span>
                        )}
                        <ChevronRight className={`w-3.5 h-3.5 transition ${active ? "opacity-100" : "opacity-30"}`} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* Main: gallery of slots */}
          <div className="lg:col-span-9">
            <div className="mb-4 flex items-baseline justify-between">
              <div>
                <div className="label-mono text-[10px]">/ {pick("SECCIÓN", "SECTION", "SEÇÃO")}</div>
                <h2 className="font-display text-2xl md:text-3xl chrome-text mt-1">
                  {activePageMeta ? activePageMeta[`label_${lang}`] || activePageMeta.label_es : ""}
                </h2>
              </div>
              {pageDrafts > 0 && (
                <div className="text-xs text-amber-200 flex items-center gap-1">
                  <CircleDot className="w-3 h-3" /> {pageDrafts} {pick("con borrador", "with draft", "com rascunho")}
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {pageSlots.map((s) => (
                <SlotCard
                  key={s.key}
                  slot={s}
                  busy={busyKey === s.key}
                  lang={lang}
                  pick={pick}
                  onFile={(file) => handleFile(s.key, file)}
                  onPublish={() => publish(s.key)}
                  onDiscard={() => discard(s.key)}
                  onReset={() => reset(s.key)}
                  onSlideUpload={(idx, file) => handleSlideUpload(s.key, idx, file)}
                  onSlideRemove={(idx) => handleSlideRemove(s.key, idx)}
                  onIntervalChange={(ms) => handleIntervalChange(s.key, ms)}
                />
              ))}
              {pageSlots.length === 0 && (
                <div className="col-span-full py-12 text-center text-white/50 label-mono">
                  {pick("Sin imágenes en esta sección", "No images in this section", "Sem imagens nesta seção")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ------------------------------ SlotCard ------------------------------ */
const VIDEO_EXTS = /\.(mp4|webm|ogv|mov|m4v)(\?.*)?$/i;
const isVideoUrl = (u) => u && (VIDEO_EXTS.test(u) || /\/video\/upload\//i.test(u));

function SlotCard({ slot, busy, lang, pick, onFile, onPublish, onDiscard, onReset, onSlideUpload, onSlideRemove, onIntervalChange }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const previewUrl = slot.draft_url || slot.published_url || slot.default_url;
  const status = slot.has_draft ? "draft" : slot.is_overridden ? "published" : "default";
  const label = slot.label?.[lang] || slot.label?.es;
  const description = slot.description?.[lang] || slot.description?.es;
  const mediaType = slot.media_type || "image";
  const previewIsVideo = isVideoUrl(previewUrl);

  // Slideshow state (draft-aware): the array shown in the 4-slot grid.
  const currentSlides = (slot.draft_slides?.length ? slot.draft_slides : slot.published_slides) || [];
  const intervalMs = slot.draft_interval_ms || slot.published_interval_ms || 5000;
  const [localInterval, setLocalInterval] = useState(intervalMs);
  useEffect(() => { setLocalInterval(intervalMs); }, [intervalMs]);

  // Accept string for <input>
  const acceptStr = mediaType === "video"
    ? "video/*"
    : mediaType === "mixed"
      ? "image/*,video/*"
      : "image/*";

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className="glass-card chrome-border p-4 flex flex-col"
      data-testid={`slot-card-${slot.key}`}>
      {/* Preview */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => !busy && inputRef.current?.click()}
        className={`relative ${slot.aspect} w-full rounded-lg overflow-hidden border border-white/10 bg-black/40 cursor-pointer group ${drag ? "ring-2 ring-cyan-400/70" : ""}`}>
        {previewUrl ? (
          previewIsVideo ? (
            <video
              src={previewUrl}
              autoPlay muted loop playsInline preload="metadata"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <img src={previewUrl} alt={label} className="absolute inset-0 w-full h-full object-cover" />
          )
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/40">
            <div className="text-center">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <div className="label-mono text-[10px]">
                {pick("SIN CONTENIDO — USA EL DEFAULT", "NO CONTENT — USES DEFAULT", "SEM CONTEÚDO — USA PADRÃO")}
              </div>
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-70 group-hover:opacity-90 transition" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/70 border border-cyan-400/40 backdrop-blur">
            <Upload className="w-4 h-4 text-cyan-300" />
            <span className="font-tech text-xs uppercase tracking-wider">
              {previewUrl ? pick("Reemplazar", "Replace", "Substituir") : pick("Subir", "Upload", "Enviar")}
            </span>
          </div>
        </div>

        {/* Status + media type badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1 items-start">
          <StatusBadge status={status} pick={pick} />
          {mediaType !== "image" && <MediaTypeBadge type={mediaType} pick={pick} />}
        </div>

        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70">
            <Loader2 className="w-6 h-6 text-cyan-300 animate-spin" />
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={acceptStr}
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid={`slot-input-${slot.key}`}
        />
      </div>

      {/* Meta */}
      <div className="mt-3">
        <h3 className="font-tech text-base text-white leading-tight">{label}</h3>
        <p className="text-xs text-white/55 leading-relaxed mt-1 line-clamp-3">{description}</p>
        <div className="mt-2 text-[10px] text-white/35 font-tech tracking-wider uppercase">{slot.key}</div>
      </div>

      {/* Slideshow — up to 4 rotating images */}
      {mediaType === "image" && (
        <SlideshowSection
          slot={slot}
          slides={currentSlides}
          intervalMs={localInterval}
          setLocalInterval={setLocalInterval}
          busy={busy}
          pick={pick}
          onSlideUpload={onSlideUpload}
          onSlideRemove={onSlideRemove}
          onIntervalChange={onIntervalChange}
        />
      )}

      {/* Actions */}
      <div className="mt-3 flex flex-wrap gap-2">
        {slot.has_draft && (
          <>
            <button
              onClick={onPublish}
              disabled={busy}
              data-testid={`publish-${slot.key}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 text-black font-tech text-[10px] uppercase tracking-wider shadow-[0_0_18px_-4px_rgba(0,240,255,0.7)] disabled:opacity-60">
              <Rocket className="w-3 h-3" /> {pick("Publicar", "Publish", "Publicar")}
            </button>
            <button
              onClick={onDiscard}
              disabled={busy}
              data-testid={`discard-${slot.key}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/20 text-white/70 hover:border-rose-400/50 hover:text-rose-200 font-tech text-[10px] uppercase tracking-wider transition disabled:opacity-60">
              <X className="w-3 h-3" /> {pick("Descartar", "Discard", "Descartar")}
            </button>
          </>
        )}
        {!slot.has_draft && slot.is_overridden && (
          <button
            onClick={onReset}
            disabled={busy}
            data-testid={`reset-${slot.key}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/20 text-white/70 hover:border-amber-400/50 hover:text-amber-200 font-tech text-[10px] uppercase tracking-wider transition disabled:opacity-60">
            <RotateCcw className="w-3 h-3" /> {pick("Restaurar original", "Reset to original", "Restaurar original")}
          </button>
        )}
        {!slot.has_draft && !slot.is_overridden && (
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-cyan-400/30 text-cyan-200/90 hover:border-cyan-400/60 hover:text-cyan-100 font-tech text-[10px] uppercase tracking-wider transition disabled:opacity-60">
            <Upload className="w-3 h-3" />
            {mediaType === "video"
              ? pick("Subir video", "Upload video", "Enviar vídeo")
              : mediaType === "mixed"
                ? pick("Subir imagen o video", "Upload image or video", "Enviar imagem ou vídeo")
                : pick("Subir imagen", "Upload image", "Enviar imagem")}
          </button>
        )}
      </div>
    </motion.div>
  );
}


/* ------------------------------ SlideshowSection ------------------------------ */
/**
 * Renders 4 fixed square slots showing the current slideshow frames + an
 * interval slider (2s – 12s). Slide #1 is the "primary" and mirrors the
 * legacy single-image draft/published URL; slots #2–#4 are optional
 * extras that rotate on top of the primary at the given cadence.
 */
function SlideshowSection({ slot, slides, intervalMs, setLocalInterval, busy, pick, onSlideUpload, onSlideRemove, onIntervalChange }) {
  const cells = Array.from({ length: 4 }, (_, i) => slides[i] || null);
  return (
    <div className="mt-4 border-t border-white/8 pt-3">
      <div className="flex items-baseline justify-between mb-2">
        <div className="label-mono text-[9px] !text-cyan-300 !tracking-[0.3em]">
          {pick("SLIDESHOW · HASTA 4", "SLIDESHOW · UP TO 4", "SLIDESHOW · ATÉ 4")}
        </div>
        <div className="text-[10px] text-white/45 font-tech">
          {slides.length}/4
        </div>
      </div>

      <div className="grid grid-cols-4 gap-1.5">
        {cells.map((cell, i) => (
          <SlideCell
            key={i}
            index={i}
            cell={cell}
            slot={slot}
            busy={busy}
            pick={pick}
            onUpload={(file) => onSlideUpload(i, file)}
            onRemove={() => onSlideRemove(i)}
          />
        ))}
      </div>

      {/* Interval slider */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-[10px] text-white/60 mb-1">
          <span className="label-mono !text-[9px] !tracking-[0.25em]">
            {pick("INTERVALO", "INTERVAL", "INTERVALO")}
          </span>
          <span className="font-mono-x text-cyan-300">{(intervalMs / 1000).toFixed(1)}s</span>
        </div>
        <input
          type="range"
          min={2000}
          max={12000}
          step={500}
          value={intervalMs}
          disabled={busy}
          onChange={(e) => setLocalInterval(Number(e.target.value))}
          onMouseUp={(e) => onIntervalChange(Number(e.target.value))}
          onTouchEnd={(e) => onIntervalChange(Number(e.target.value))}
          className="w-full accent-cyan-400 h-1"
          data-testid={`interval-${slot.key}`}
        />
      </div>
    </div>
  );
}

/* ------------------------------ SlideCell ------------------------------ */
function SlideCell({ index, cell, slot, busy, pick, onUpload, onRemove }) {
  const inputRef = useRef(null);
  const url = cell?.url;
  return (
    <div className="relative aspect-square rounded-md overflow-hidden border border-white/10 bg-black/50 group">
      {url ? (
        <>
          <img src={url} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            data-testid={`slide-remove-${slot.key}-${index}`}
            title={pick("Quitar", "Remove", "Remover")}
            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 border border-white/20 hover:border-rose-400/70 hover:text-rose-200 flex items-center justify-center text-white/70 opacity-0 group-hover:opacity-100 transition"
          >
            <X className="w-3 h-3" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-black/80 to-transparent flex items-center justify-center">
            <span className="font-mono-x text-[9px] text-cyan-300">#{index + 1}</span>
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={() => !busy && inputRef.current?.click()}
          disabled={busy}
          data-testid={`slide-upload-${slot.key}-${index}`}
          className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-white/40 hover:text-cyan-300 hover:bg-cyan-400/5 border-dashed border border-white/10 hover:border-cyan-400/40 transition"
        >
          <Upload className="w-3.5 h-3.5" />
          <span className="font-mono-x text-[9px]">#{index + 1}</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onUpload(e.target.files?.[0])}
      />
    </div>
  );
}


/* ------------------------------ MediaTypeBadge ------------------------------ */
function MediaTypeBadge({ type, pick }) {
  const label = type === "video"
    ? pick("VIDEO", "VIDEO", "VÍDEO")
    : pick("IMAGEN O VIDEO", "IMAGE OR VIDEO", "IMAGEM OU VÍDEO");
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-400/20 border border-fuchsia-400/50 text-fuchsia-100 font-tech text-[9px] uppercase tracking-wider backdrop-blur">
      {label}
    </span>
  );
}


/* ------------------------------ StatusBadge ------------------------------ */
function StatusBadge({ status, pick }) {
  if (status === "draft") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/20 border border-amber-400/50 text-amber-100 font-tech text-[9px] uppercase tracking-wider backdrop-blur">
        <AlertCircle className="w-2.5 h-2.5" /> {pick("BORRADOR", "DRAFT", "RASCUNHO")}
      </span>
    );
  }
  if (status === "published") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-400/20 border border-emerald-400/50 text-emerald-100 font-tech text-[9px] uppercase tracking-wider backdrop-blur">
        <CheckCircle2 className="w-2.5 h-2.5" /> {pick("PUBLICADA", "LIVE", "PUBLICADA")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 border border-white/20 text-white/70 font-tech text-[9px] uppercase tracking-wider backdrop-blur">
      <ImageIcon className="w-2.5 h-2.5" /> {pick("ORIGINAL", "ORIGINAL", "ORIGINAL")}
    </span>
  );
}
